import * as React from 'react'
import { Carousel } from 'DVWebNodeUI'
import Header from './Header/Header'
import Card from './Card/Card'
import * as style from './FacetedCarousel.module.scss'
import * as csm from '../../../utils/csm'
import { leftToScrollPx } from '../../../utils/scrolling'
import Observer from 'react-intersection-observer'
import * as cn from 'classnames'
import { CollectionProps } from '../Collection';
import { StorefrontStateItem } from '../../../containers/Storefront';
import { safeBool } from 'DVWebNodeUtils'

const offsetPx = 10
const spacingOffsetPx = 40 // Modify only if the carousel component spacing changes
const paddingSizePx = 13
const borderRadiusPx = 3

interface FacetedCarouselProps extends CollectionProps {
  isIsolatedCarousel?: boolean
}

interface FacetedCarouselState {
  items: StorefrontStateItem[]
  isEditActive: boolean
  isDirty: boolean
  shadowOn: boolean
  truncateItemList: boolean
  hasHoveredItem: boolean
  hasFocus: boolean
}

class FacetedCarousel extends React.PureComponent<FacetedCarouselProps, FacetedCarouselState> {
  state: FacetedCarouselState
  isGettingEnriched: boolean
  isPartiallyEnriched: boolean
  isFullyEnriched: boolean
  fetch: Function
  carouselRef: HTMLDivElement
  shadowRef: HTMLDivElement
  outerWidth: number

  constructor(props: FacetedCarouselProps) {
    super(props)
    this.state = {
      items: props && props.items,
      isEditActive: false,
      isDirty: false,
      shadowOn: false, // enabling Shadow Effect only on Client side to look good also when Js is disabled
      truncateItemList: props.featureSwitches.delayItemsRendering,
      hasHoveredItem: true, // Set to true to enable least-bad hover UX without JS.
      hasFocus: false
    }
    this.outerWidth = 0 // 0 means unset or full width
    this.isGettingEnriched = false
    this.isPartiallyEnriched = false
    this.isFullyEnriched = false
    this.fetch = props.fetch

    this.toggleEdit = this.toggleEdit.bind(this)
    this.carouselScrollInteractionHandler = this.carouselScrollInteractionHandler.bind(this)
    this.carouselNavClickInteractionHandler = this.carouselNavClickInteractionHandler.bind(this)
    this.carouselInteractionHandler = this.carouselInteractionHandler.bind(this)
  }


  componentDidMount() {
    this.setState({ hasHoveredItem: false })
    if (!safeBool(this.props.isEntitled) && !this.props.hasAltTheme && !this.props.isPhablet) {
      this.setState({ shadowOn: true })
      const carousel = this.carouselRef.querySelector('ul')
      const packshotWidth = carousel.querySelector('li').scrollWidth
      const packshotLength = carousel.querySelectorAll('li').length

      if (carousel.offsetWidth > packshotWidth * packshotLength) {
        this.outerWidth = packshotWidth * packshotLength + paddingSizePx
        window.requestAnimationFrame(() => {
          this.shadowRef.style.width = `${packshotWidth * packshotLength + paddingSizePx}px`
          this.applyCSSRulesToShadow(`translate(${spacingOffsetPx}px)`, `${borderRadiusPx}px`)
        })
      } else {
        this.initShadowEvents(carousel)
      }
    }
  }

  componentDidCatch(error: Error) {
    csm.count('DVWeb::Storefront::JSFatal::FacetedCarousel')
    csm.logError(error, '[DVWeb::Storefront] Error while trying to render a FacetedCarousel')
  }

  applyCSSRulesToShadow(translate: string, borderRadius: string) {
    this.shadowRef.style.transform = translate
    this.shadowRef.style.borderRadius = borderRadius
  }

  initShadowEvents(carousel: HTMLElement | HTMLUListElement) {
    const calculateStyle = () => {
      const scrollStatusPx = leftToScrollPx(carousel)

      if (scrollStatusPx <= spacingOffsetPx) {
        if (scrollStatusPx < spacingOffsetPx) {
          this.applyCSSRulesToShadow(`translate(${((scrollStatusPx) % spacingOffsetPx) - spacingOffsetPx}px)`, `${borderRadiusPx}px`)
        }
      } else if (carousel.scrollLeft > 0 && (scrollStatusPx) >= spacingOffsetPx) {
        if (carousel.scrollLeft <= spacingOffsetPx) {
          this.applyCSSRulesToShadow(`translate(${spacingOffsetPx - carousel.scrollLeft}px)`, `${borderRadiusPx}px`)
        } else {
          this.applyCSSRulesToShadow('translate(0)', '0')
        }
      } else if (carousel.scrollLeft < offsetPx) {
        this.applyCSSRulesToShadow(`translate(${spacingOffsetPx}px)`, `${borderRadiusPx}px`)
      }
    }

    window.requestAnimationFrame(() => calculateStyle())
    carousel.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => calculateStyle())
    })
  }

  toggleEdit() {
    this.setState({ isEditActive: !this.state.isEditActive })
  }

  setCollectionDirty() {
    if (!this.state.isDirty) {
      this.setState({ isDirty: true, truncateItemList: false })
    }
  }

  carouselScrollInteractionHandler() {
    csm.count('CarouselScroll')
    this.carouselInteractionHandler()
  }

  carouselNavClickInteractionHandler() {
    csm.count('CarouselPagination')
    this.carouselInteractionHandler()
  }

  carouselInteractionHandler() {
    if (!this.props.canLazyLoad || this.isGettingEnriched) {
      this.setCollectionDirty()
    } else if (!this.isFullyEnriched && this.props.enrichCollection) {
      this.isGettingEnriched = true
      this.props.enrichCollection(this.props.collectionKey, true).then(() => {
        this.isGettingEnriched = false
        this.isFullyEnriched = true
        this.setCollectionDirty()
      })
    }
  }

  getCarouselItems(items: StorefrontStateItem[], inView: boolean) {
    const LAZY_THRESHOLD = 8
    const SQUARE_LAZY_THRESHOLD = 14
    const isTwinHeroCarouselType = this.props.collectionType === 'TwinHero'
    const isSquareCarouselType = this.props.collectionType === 'SquareCarousel'
    const thisCarouselLazyThreshold = isSquareCarouselType ? SQUARE_LAZY_THRESHOLD : LAZY_THRESHOLD
    const truncatedItems = this.state.truncateItemList ? items.slice(0, thisCarouselLazyThreshold + 1) : items
    const carouselItems = truncatedItems.filter(item => item).map((item, i) => {
      const isAfterThreshold = i > thisCarouselLazyThreshold
      return (<Card
        key={`${item.title} ${i}`}
        {...item}
        collectionType={this.props.collectionType}
        isTwinHero={isTwinHeroCarouselType}
        isSquare={isSquareCarouselType}
        isEditActive={this.state.isEditActive}
        removeItem={() => this.props.removeItem(i, this.props.collectionKey)}
        triggerCarouselLazyload={this.carouselInteractionHandler}
        isLazy={!this.state.isDirty && isAfterThreshold}
        hoverClass={this.props.hoverClass}
        featureSwitches={this.props.featureSwitches}
        description={item.title}
        pos={i}
        updateChannelItem={this.props.updateChannelItem}
        fetch={this.props.fetch}
        canLivePlayback={this.props.canLivePlayback}
        enrichSingleItem={this.props.enrichSingleItem}
        showProgressBar={Boolean(this.props.edit)}
        isVerticallyVisible={inView}
        isIsolatedCarousel={this.props.isIsolatedCarousel}
        showPlayButton={this.props.showPlayButton}
        hasAltTheme={!safeBool(this.props.isEntitled) || this.props.hasAltTheme}
        isPhablet={this.props.isPhablet}
        onMouseLeave={() => this.setState({ hasHoveredItem: false })}
        onMouseOver={() => this.setState({ hasHoveredItem: true })}
      />)
    })

    return carouselItems
  }

  render() {
    const props = this.props
    const items = this.props.items
    
    return (
      <div
        onFocus={() => this.setState({hasFocus: true})}
        onBlur={() => this.setState({hasFocus: false})}
        className={cn(style.FacetedCarousel, 'tst-faceted-carousel', {
          'tst-editable-carousel': Boolean(this.props.edit),
          ['mobile-collection']: props.isPhablet,
          ['min-width-collection']: props.featureSwitches &&
            props.featureSwitches.requiresMinimumPageWidth,
          [style.isolatedCarousel]: this.props.isIsolatedCarousel,
          [style.overflowVisible]: this.props.featureSwitches.hover2019,
          [style.AltTheme]: !safeBool(this.props.isEntitled) && !this.props.hasAltTheme, // Local Theme
          'dvuiAltTheme': !safeBool(this.props.isEntitled) || this.props.hasAltTheme // DVUI Theme
        })}
      >
        <div className={style.Shadow} ref={
          ref => {
            this.shadowRef = ref
          }
        } />
        <Header
          {...props}
          toggleEdit={this.toggleEdit}
          isEditActive={this.state.isEditActive}
          noMarginLeft={this.props.isIsolatedCarousel}
          hasAltTheme={!safeBool(this.props.isEntitled) || this.props.hasAltTheme}
          seeMoreDescription={this.props.seeMoreDescription}
          shortTitleWidth={this.outerWidth}
        />
        <Observer triggerOnce={true}>
          {(inview) => <div
            ref={ref => {
              this.carouselRef = ref
            }}
            onScroll={this.carouselScrollInteractionHandler}>
            <Carousel
              unusableVisibleWidth={parseInt(style.unusedCarouselSpace, 10)}
              moveToListener={this.carouselNavClickInteractionHandler}
              wrapChildren={false}
              className={cn({
                [style.hoverControl]: this.state.hasHoveredItem,
                [style.focusControl]: this.state.hasFocus
              })}
              leftScrollButtonClassName={style.scrollButton}
              rightScrollButtonClassName={style.scrollButton}
            >
              {this.getCarouselItems(items, inview)}
            </Carousel>
          </div>}
        </Observer>
      </div>
    )
  }
}

export default FacetedCarousel
